import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RefreshCw, Wrench, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import MaintenanceRow from "./maintenance/MaintenanceRow";
import { TRACKED_JOBS, type TrackedJob, type CronRow, type RunLogRow, type FailureInfo } from "./maintenance/types";

const MaintenanceRunsWidget = () => {
  const [cronRows, setCronRows] = useState<Record<string, CronRow | undefined>>({});
  const [runLogs, setRunLogs] = useState<Record<string, RunLogRow | undefined>>({});
  const [loading, setLoading] = useState(true);
  const [runningFn, setRunningFn] = useState<string | null>(null);
  const [confirmJob, setConfirmJob] = useState<TrackedJob | null>(null);
  const [failureDetails, setFailureDetails] = useState<Record<string, FailureInfo | undefined>>({});
  const [openDetails, setOpenDetails] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    setLoading(true);
    const [cronRes, logRes] = await Promise.all([
      supabase.rpc("get_all_cron_jobs_with_latest_run" as never),
      supabase
        .from("maintenance_run_log")
        .select("function_name, affected_count, success, ran_at")
        .in("function_name", TRACKED_JOBS.map((j) => j.functionName))
        .order("ran_at", { ascending: false })
        .limit(50),
    ]);

    const cronMap: Record<string, CronRow | undefined> = {};
    ((cronRes.data as CronRow[] | null) ?? []).forEach((r) => { cronMap[r.jobname] = r; });
    setCronRows(cronMap);

    const logMap: Record<string, RunLogRow | undefined> = {};
    ((logRes.data as RunLogRow[] | null) ?? []).forEach((r) => {
      if (!logMap[r.function_name]) logMap[r.function_name] = r;
    });
    setRunLogs(logMap);

    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const runNow = useCallback(async (functionName: string, label: string) => {
    setRunningFn(functionName);
    setFailureDetails((d) => ({ ...d, [functionName]: undefined }));
    try {
      if (functionName === "prune-maintenance-run-log") {
        const { data, error } = await supabase.rpc("prune_maintenance_run_log" as never);
        if (error) {
          setFailureDetails((d) => ({
            ...d,
            [functionName]: { message: error.message, raw: { error, data } },
          }));
          setOpenDetails((o) => ({ ...o, [functionName]: true }));
          throw error;
        }
        toast.success(`${label} ran successfully`, { description: `${data ?? 0} rows pruned` });
        await load();
        return;
      }

      const { data, error } = await supabase.functions.invoke(functionName, { body: {} });
      if (error) {
        setFailureDetails((d) => ({
          ...d,
          [functionName]: { message: error.message ?? "Unknown error", raw: { error, data } },
        }));
        setOpenDetails((o) => ({ ...o, [functionName]: true }));
        throw error;
      }
      toast.success(`${label} ran successfully`);
      await load();
    } catch (err) {
      toast.error(`${label} failed`, {
        description: err instanceof Error ? err.message : "Unknown error",
      });
      setFailureDetails((d) =>
        d[functionName]
          ? d
          : { ...d, [functionName]: { message: err instanceof Error ? err.message : String(err), raw: err } }
      );
      setOpenDetails((o) => ({ ...o, [functionName]: true }));
      await load();
    } finally {
      setRunningFn(null);
    }
  }, [load]);

  const handleTrigger = (job: TrackedJob) => {
    if (job.destructive) {
      setConfirmJob(job);
    } else {
      runNow(job.functionName, job.label);
    }
  };

  return (
    <>
      <Card className="border-border">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Wrench size={15} className="text-muted-foreground" />
              Maintenance Job Runs
            </CardTitle>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={load} disabled={loading}>
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Last run + records affected for each maintenance function
          </p>
        </CardHeader>
        <CardContent className="space-y-2">
          {TRACKED_JOBS.map((job) => (
            <MaintenanceRow
              key={job.name}
              job={job}
              cron={cronRows[job.name]}
              log={runLogs[job.functionName]}
              isRunning={runningFn === job.functionName}
              anyRunning={runningFn !== null}
              failure={failureDetails[job.functionName]}
              detailsOpen={!!openDetails[job.functionName]}
              onTrigger={handleTrigger}
              onDetailsOpenChange={(open) => setOpenDetails((o) => ({ ...o, [job.functionName]: open }))}
            />
          ))}
        </CardContent>
      </Card>

      <AlertDialog open={confirmJob !== null} onOpenChange={(open) => { if (!open) setConfirmJob(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle size={18} className="text-destructive" />
              Run {confirmJob?.label}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmJob?.destructiveWarning ?? "This action may modify or delete data."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (confirmJob) {
                  const job = confirmJob;
                  setConfirmJob(null);
                  runNow(job.functionName, job.label);
                }
              }}
            >
              Yes, run now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default MaintenanceRunsWidget;
